/**
 * PrivacyPolicyPage.tsx
 *
 * Public privacy policy, "/privacy". Not behind auth.
 *
 * This is the URL registered as the privacy policy of the Google OAuth app
 * (Google Cloud project "prospera", Branding). Google's verification reads it
 * and requires the "Google User Data" section — what each scope reads, how it
 * is used, stored, shared and deleted — and the Limited Use statement word for
 * word. Keep both in step with the scopes requested in
 * MarketingIntegrationsController before changing either.
 *
 * Sections 1–5 and 7–13 are the policy published at prospera.ai/privacy.
 * Static on purpose: no API call.
 */

type Section = {
  heading: string;
  body: string[];
  list?: string[];
  /** Renders the Limited Use statement, which links to Google's policy. */
  limitedUse?: boolean;
};

const LAST_UPDATED = 'October 21, 2026';

const GOOGLE_POLICY_URL =
  'https://developers.google.com/terms/api-services-user-data-policy';

const SECTIONS: Section[] = [
  {
    heading: '1. Introduction',
    body: [
      'Prospera.ai ("Prospera," "we," "us," or "our") provides a practice-management platform for healthcare providers. This Privacy Policy describes how we collect, use, disclose, and protect information when you visit www.prospera.ai (the "Site") or use our services (the "Services"). By using the Site or Services, you agree to the practices described here.',
    ],
  },
  {
    heading: '2. Information We Collect',
    body: [
      'We collect information you provide directly, information generated through your use of the Services, and information from a limited set of service providers.',
    ],
    list: [
      'Account information: name, email, phone, practice details, and login credentials.',
      'Practice and patient data uploaded or generated by providers using the Services, including clinical notes, lab results, communications, and billing records.',
      'Usage data: device type, browser, IP address, pages viewed, and interactions with the Services.',
      'Support and communications: messages you send to us, including through demo requests and email.',
      'Data from connected Google accounts: if a practice administrator connects Google Ads, Google Analytics or Google Search Console, we receive the data described in section 6 ("Google User Data").',
    ],
  },
  {
    heading: '3. How We Use Information',
    body: [
      'We use information to operate, secure, and improve the Services; to authenticate users; to provide support; to process payments; to communicate about the Services; and to comply with legal obligations.',
    ],
  },
  {
    heading: '4. Protected Health Information (PHI)',
    body: [
      'When our customers use the Services to store or process PHI, we act as a Business Associate under HIPAA and as a service provider under PIPEDA. We handle PHI only as permitted by the applicable Business Associate Agreement or Data Processing Agreement with the practice. Practices are the controllers of their patient records.',
    ],
  },
  {
    heading: '5. How We Share Information',
    body: ['We do not sell personal information. We share information only with:'],
    list: [
      'Service providers that host infrastructure, process payments, deliver communications, and provide analytics under written contracts.',
      'Integrated providers you connect (e.g., lab networks) at your direction.',
      'Authorities when required by law or to protect the rights, safety, and property of Prospera, our customers, and their patients.',
      'A successor in the event of a merger, acquisition, or reorganization.',
    ],
  },
  {
    heading: '6. Google User Data',
    body: [
      "Prospera.ai lets an authorized administrator of a practice connect the practice's own Google accounts so its marketing performance can be shown inside Prospera.ai. We access Google user data only after the administrator signs in with Google and grants permission on Google's consent screen.",
      'What we access:',
    ],
    list: [
      'Google Ads (https://www.googleapis.com/auth/adwords): the list of Google Ads accounts the user can access, and campaign, ad group, ad and keyword performance data (impressions, clicks, cost, conversions) for the account the administrator selects.',
      'Google Analytics (https://www.googleapis.com/auth/analytics.readonly): the list of Analytics properties the user can access, and website traffic and conversion reports for the property the administrator selects.',
      'Google Search Console (https://www.googleapis.com/auth/webmasters.readonly): the list of verified sites, and search performance data (queries, pages, impressions, clicks, position) for the site the administrator selects.',
      'This connection does not access Gmail, Google Drive, Google Contacts, Google Calendar or any other Google data.',
    ],
  },
  {
    heading: '6.1 How we use, store and share Google user data',
    body: [
      'We use Google user data only to show the practice its own marketing, advertising and search performance in Prospera.ai dashboards and reports, and to provide features the practice requests. We do not use it for advertising, we do not sell it, and we do not use it to build profiles of individuals.',
      "When a user of the practice asks Prospera.ai's AI assistant about marketing performance, the assistant analyses that practice's Google Ads and Analytics figures to answer the question. This runs on AI models hosted within Prospera.ai's infrastructure provider; the data is processed only for the practice that asked and is not used to train AI models.",
      "OAuth tokens issued by Google are encrypted at rest and stored only in the connected practice's account. They are sent only to Google, over HTTPS. Report data is requested from Google when a user views a report, and we keep it briefly, up to 10 minutes, to speed up repeat views.",
      'We do not share Google user data with third parties, except with the infrastructure providers that host Prospera.ai under written contracts, as required by law, or as part of a merger or acquisition with prior notice to users. Google user data is never visible to other practices.',
    ],
  },
  {
    heading: '6.2 Retention and deletion of Google user data',
    body: [
      'We keep Google tokens while the integration stays connected. When an administrator selects Disconnect in Admin → Marketing, we delete the stored refresh token immediately; any short-lived access token already issued expires within one hour. A user can also revoke our access at any time at https://myaccount.google.com/permissions. To request deletion of any remaining data, contact privacy@prospera.ai, and we will delete it within 30 days.',
    ],
  },
  {
    heading: '6.3 Limited Use',
    body: [],
    limitedUse: true,
  },
  {
    heading: '7. Data Security',
    body: [
      'We maintain administrative, technical, and physical safeguards designed to protect information, including encryption in transit and at rest, role-based access controls, audit logging, and regular security reviews. No system is fully secure; we work continuously to improve our controls.',
    ],
  },
  {
    heading: '8. Data Retention',
    body: [
      "We retain personal information for as long as needed to provide the Services, comply with legal obligations, resolve disputes, and enforce agreements. Practice-owned records are retained according to the practice's instructions and applicable retention requirements.",
    ],
  },
  {
    heading: '9. Your Rights',
    body: [
      'Depending on your location, you may have rights to access, correct, delete, or port personal information, and to object to or restrict certain processing. Patients should direct requests about their records to their healthcare provider. To exercise rights regarding information Prospera holds about you directly, contact us using the details below.',
    ],
  },
  {
    heading: '10. International Transfers',
    body: [
      'Prospera operates in Canada and the United States. Information may be processed in either country subject to appropriate safeguards and applicable law.',
    ],
  },
  {
    heading: '11. Children',
    body: [
      'The Services are not directed to children under 13, and we do not knowingly collect personal information from children except as authorized by a treating practice.',
    ],
  },
  {
    heading: '12. Changes to This Policy',
    body: [
      'We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date below and, when appropriate, provide additional notice.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="border-b border-slate-200">
        <div className="mx-auto flex h-16 max-w-3xl items-center px-4">
          <a href="https://prospera.ai" className="text-lg font-extrabold text-indigo-700 tracking-tight">
            Prospera AI
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12 md:py-16">
        <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">Privacy Policy</h1>
        <p className="mt-2 text-sm text-slate-500">Last updated: {LAST_UPDATED}</p>

        <div className="mt-8 space-y-8">
          {SECTIONS.map((section) => (
            <section key={section.heading}>
              <h2 className="text-xl font-bold">{section.heading}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph} className="mt-3 leading-relaxed text-slate-700">
                  {paragraph}
                </p>
              ))}
              {section.list && (
                <ul className="mt-3 list-disc space-y-1.5 pl-6 text-slate-700 [overflow-wrap:anywhere]">
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              {section.limitedUse && (
                <p className="mt-3 leading-relaxed text-slate-700">
                  Prospera.ai&apos;s use and transfer to any other app of information received from
                  Google APIs will adhere to the{' '}
                  <a href={GOOGLE_POLICY_URL} className="text-blue-600 hover:underline">
                    Google API Services User Data Policy
                  </a>
                  , including the Limited Use requirements.
                </p>
              )}
            </section>
          ))}

          <section>
            <h2 className="text-xl font-bold">13. Contact Us</h2>
            <p className="mt-3 leading-relaxed text-slate-700">
              If you have questions or requests about this policy, contact us:
            </p>
            <p className="mt-3 leading-relaxed text-slate-700">Prospera.ai</p>
            <p className="mt-1 leading-relaxed text-slate-700">
              Email:{' '}
              <a href="mailto:privacy@prospera.ai" className="text-blue-600 hover:underline">
                privacy@prospera.ai
              </a>
            </p>
            <p className="mt-1 leading-relaxed text-slate-700">
              Phone:{' '}
              <a href="tel:+13105896380" className="text-blue-600 hover:underline">
                (310) 589-6380
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
